import type { components } from '@multaqa/api-client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { type InventoryChange, invalidateInventory } from '@/lib/inventory-invalidation'

/**
 * The stock transfer API (WP01 step 10). Every figure — what is still in
 * transit, what the caller may do next — is the server's (`abilities`,
 * `quantity_in_transit`); nothing here derives a rule. Keys live under
 * ['inventory', company, 'transfers'], the family the invalidation map owns.
 */

export type StockTransfer = components['schemas']['StockTransferResource']
export type StockTransferLine = components['schemas']['StockTransferLineResource']
export type TransferStatus =
  | 'requested'
  | 'approved'
  | 'rejected'
  | 'picked'
  | 'in_transit'
  | 'received'
  | 'completed'
  | 'cancelled'

export const TRANSFER_STATUSES: TransferStatus[] = [
  'requested',
  'approved',
  'picked',
  'in_transit',
  'received',
  'completed',
  'rejected',
  'cancelled',
]

export type TransferFilters = {
  status?: TransferStatus
  from?: string
  to?: string
  q?: string
  page?: number
  perPage?: number
}

export const transferKeys = {
  all: (companyId: string | null) => ['inventory', companyId, 'transfers'] as const,
  list: (companyId: string | null, filters: TransferFilters) =>
    ['inventory', companyId, 'transfers', 'list', filters] as const,
  detail: (companyId: string | null, transferId: string) =>
    ['inventory', companyId, 'transfers', 'detail', transferId] as const,
}

const headers = (companyId: string, idempotencyKey?: string) => ({
  'X-Company-Id': companyId,
  ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
})

export function useTransfers(companyId: string | null, filters: TransferFilters) {
  return useQuery({
    queryKey: transferKeys.list(companyId, filters),
    enabled: Boolean(companyId),
    placeholderData: (previous) => previous,
    queryFn: async () => {
      const response = await api.GET('/inventory/transfers', {
        headers: headers(companyId ?? ''),
        params: {
          query: {
            status: filters.status,
            source_warehouse_id: filters.from,
            destination_warehouse_id: filters.to,
            q: filters.q,
            page: filters.page,
            per_page: filters.perPage ?? 25,
          },
        },
      })
      if (!response.data) throw new Error('GET /inventory/transfers returned no body')
      return response.data
    },
  })
}

const SETTLED: string[] = ['completed', 'rejected', 'cancelled']
const isActiveTransfer = (status: string | undefined) =>
  status !== undefined && !SETTLED.includes(status)

/**
 * One transfer. While it is still moving it polls, so the other warehouse's
 * pick, dispatch or receipt shows up without a reload; a settled one does not.
 */
export function useTransfer(companyId: string | null, transferId: string) {
  return useQuery({
    queryKey: transferKeys.detail(companyId, transferId),
    enabled: Boolean(companyId),
    refetchInterval: (query) => (isActiveTransfer(query.state.data?.status) ? 30_000 : false),
    refetchOnWindowFocus: (query) => isActiveTransfer(query.state.data?.status),
    queryFn: async (): Promise<StockTransfer> => {
      const response = await api.GET('/inventory/transfers/{transfer}', {
        headers: headers(companyId ?? ''),
        params: { path: { transfer: transferId } },
      })
      if (!response.data) throw new Error('GET /inventory/transfers/{transfer} returned no body')
      return response.data.data
    },
  })
}

export type TransferLineAvailability = components['schemas']['TransferLineAvailabilityResource']

/**
 * What each line holds and what is free at the source now (WP03), for the
 * approve dialog. Under the stock family's 'transfer-availability' key, so any
 * stock move refreshes it; always refetched when the dialog opens.
 */
export function useTransferAvailability(
  companyId: string | null,
  transferId: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['inventory', companyId, 'transfer-availability', transferId],
    enabled: Boolean(companyId) && enabled,
    staleTime: 0,
    queryFn: async (): Promise<TransferLineAvailability[]> => {
      const response = await api.GET('/inventory/transfers/{transfer}/availability', {
        headers: headers(companyId ?? ''),
        params: { path: { transfer: transferId } },
      })
      if (!response.data)
        throw new Error('GET /inventory/transfers/{transfer}/availability returned no body')
      return response.data.data
    },
  })
}

export type StockRow = { warehouseId: string; locationId: string; variantId: string; lotId: string }

/**
 * Free stock at the exact row a draft line would ship from — the figure
 * approval checks (WP03 D10). A hint only: the server decides.
 */
export function useStockFree(companyId: string | null, row: StockRow, enabled: boolean) {
  return useQuery({
    queryKey: ['inventory', companyId, 'stock-free', row],
    enabled: Boolean(companyId) && enabled && row.warehouseId !== '' && row.variantId !== '',
    staleTime: 0,
    queryFn: async (): Promise<string> => {
      const response = await api.GET('/inventory/stock-free', {
        headers: headers(companyId ?? ''),
        params: {
          query: {
            warehouse_id: row.warehouseId,
            variant_id: row.variantId,
            location_id: row.locationId || undefined,
            lot_id: row.lotId || undefined,
          },
        },
      })
      if (!response.data) throw new Error('GET /inventory/stock-free returned no body')
      return response.data.data.free
    },
  })
}

export type CreateTransferBody = components['schemas']['StockTransferStoreRequest']

export function useCreateTransfer(companyId: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { body: CreateTransferBody; idempotencyKey: string }) => {
      const response = await api.POST('/inventory/transfers', {
        headers: headers(companyId ?? '', input.idempotencyKey),
        body: input.body,
      })
      return expectTransfer(response.data)
    },
    onSuccess: () => void invalidateInventory(queryClient, companyId, 'transfer.drafted'),
  })
}

type Step = 'picks' | 'dispatches' | 'cancellations'
const STEP_CHANGE: Record<Step, InventoryChange> = {
  picks: 'transfer.moved',
  dispatches: 'transfer.moved',
  cancellations: 'transfer.decided',
}

/** A transition with no body: pick, dispatch or cancel. */
export function useTransferStep(companyId: string | null, step: Step) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      transferId: string
      idempotencyKey: string
      /** WP11: the units a dispatch names, per line. */
      serials?: { line_id: string; serial_numbers: string[] }[]
    }) => {
      const params = { path: { transfer: input.transferId } }
      const init = { headers: headers(companyId ?? '', input.idempotencyKey), params }
      const response =
        step === 'picks'
          ? await api.POST('/inventory/transfers/{transfer}/picks', init)
          : step === 'dispatches'
            ? await api.POST('/inventory/transfers/{transfer}/dispatches', {
                ...init,
                body: input.serials?.length ? { serials: input.serials } : {},
              })
            : await api.POST('/inventory/transfers/{transfer}/cancellations', init)
      return expectTransfer(response.data)
    },
    onSuccess: () => void invalidateInventory(queryClient, companyId, STEP_CHANGE[step]),
  })
}

export function useDecideTransfer(companyId: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      transferId: string
      decision: 'approved' | 'rejected'
      comment: string
      idempotencyKey: string
    }) => {
      const response = await api.POST('/inventory/transfers/{transfer}/approvals', {
        headers: headers(companyId ?? '', input.idempotencyKey),
        params: { path: { transfer: input.transferId } },
        body: { decision: input.decision, comment: input.comment.trim() || null },
      })
      return expectTransfer(response.data)
    },
    onSuccess: () => void invalidateInventory(queryClient, companyId, 'transfer.decided'),
  })
}

/** Quantities are decimal strings in the form and numbers on the wire, as the receipt form sends them. */
export type ReceiptLineInput = {
  line_id: string
  quantity_received: number
  discrepancy_reason: string | null
  write_off: boolean
  /** WP11: the units that arrived, for a product tracked by serial. */
  serial_numbers?: string[]
}

function expectTransfer(body: { data: StockTransfer } | undefined): StockTransfer {
  if (!body) throw new Error('The transfer endpoint returned no body')
  return body.data
}

export function useReceiveTransfer(companyId: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      transferId: string
      lines: ReceiptLineInput[]
      idempotencyKey: string
    }) => {
      const response = await api.POST('/inventory/transfers/{transfer}/receipts', {
        headers: headers(companyId ?? '', input.idempotencyKey),
        params: { path: { transfer: input.transferId } },
        body: { lines: input.lines },
      })
      return expectTransfer(response.data)
    },
    onSuccess: () => void invalidateInventory(queryClient, companyId, 'transfer.moved'),
  })
}
